import coverageCodes from '@/assets/data/coverage-codes.json';

export const ExpirationState = {
  Active: 'Active',
  ExpiredDate: 'Expired (Date)',
  ExpiredHours: 'Expired (Hours)',
  CloseDate: 'Close to Expiring (Date)',
  CloseHours: 'Close to Expiring (Hours)',
  Expired: 'Expired',
  Close: 'Close to Expiring',
} as const;

export type ExpirationState = (typeof ExpirationState)[keyof typeof ExpirationState];

export const getCoverageHours = (coverageCode: keyof typeof coverageCodes): number | undefined => {
  const coverage = coverageCodes[coverageCode];
  if (!coverage) {
    console.warn(`Coverage code ${coverageCode} not found in coverage codes data.`);
    return undefined;
  }
  return coverage['CoverageHours'];
};

export const getExpirationState = (
  _coverageCode: keyof typeof coverageCodes,
  _expirationDate: string,
  _operatingHours: number
): ExpirationState | null => {
  const coverage = coverageCodes[_coverageCode];
  if (!coverage) return null;

  const closeToExpireLimitDays = 90;
  const closeToExpireLimitHours = 250;

  const now = new Date();
  const expirationDate = new Date(_expirationDate);

  const daysUntilExpiration = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  const isExpiredByDate = expirationDate < now;
  const isCloseDateExpiring = daysUntilExpiration < closeToExpireLimitDays;
  const isExpiredByHours = _operatingHours !== undefined && coverage['CoverageHours'] <= _operatingHours;
  const isCloseHoursExpiring =
    _operatingHours !== undefined && coverage['CoverageHours'] - _operatingHours < closeToExpireLimitHours;

  // Check for expired states first
  if (isExpiredByDate && isExpiredByHours) {
    return ExpirationState.Expired;
  }
  if (isExpiredByDate) {
    return ExpirationState.ExpiredDate;
  }
  if (isExpiredByHours) {
    return ExpirationState.ExpiredHours;
  }

  // Check for close to expiring states
  if (isCloseDateExpiring && isCloseHoursExpiring) {
    return ExpirationState.Close;
  }
  if (isCloseDateExpiring) {
    return ExpirationState.CloseDate;
  }
  if (isCloseHoursExpiring) {
    return ExpirationState.CloseHours;
  }

  return ExpirationState.Active;
};

export const getCoverageType = (coverageCode: keyof typeof coverageCodes) => {
  const coverage = coverageCodes[coverageCode];
  if (!coverage) return 'Unknown';

  let coverageType = coverage['IconType'];

  if (coverage['CoverageHours'] === 99999) {
    coverageType += ' Unlimited Hours';
  }

  return coverageType;
};

export const getShortDescription = (description: string) => description.split('-')[0];

// export const getUniqueWithPriorityState = (prioCoverage: ICoverageWithState[]) =>
//   prioCoverage.reduce((all, coverage) => {
//     const existingIndex = all.findIndex((c) => getType(c) === getType(coverage));

//     if (existingIndex === -1) {
//       all.push(coverage);
//       return all;
//     }

//     if (all[existingIndex].state & ExpirationState.Close) {
//       return all;
//     }

//     if (!(coverage.state & ExpirationState.Expired)) {
//       all[existingIndex] = coverage;
//     }
//     return all;
//   }, [] as ICoverageWithState[]);

// export const getWithState = (
//   coverage: TCoverageWithCodeData[],
//   operatingHours: ILatestOperatingHours[] | undefined
// ): ICoverageWithState[] =>
//   coverage.map((cov) => {
//     const hours = operatingHours?.find((h) => h.ChassisId === getChassisId(cov));
//     return {
//       ...cov,
//       state: getExpirationState(cov, hours?.Value ?? 0),
//     };
//   });

// export const areAllExpired = (coverage: ICoverageWithState[]) =>
//   coverage.every((c) => Boolean(c.state & ExpirationState.Expired));

// export const getExtended = <T extends TCoverageWithCodeData | ICoverageWithState>(
//   coverage: T[] = []
// ): T[] => coverage.filter((c) => c['Icon text'] === 'Extended');

// export const getRegular = <T extends TCoverageWithCodeData | ICoverageWithState>(
//   coverage: T[] = []
// ): T[] => coverage.filter((c) => c['Icon text'] !== 'Extended');
